<template>
  <Teleport to="body">
    <div class="cf-outline">
      <Transition name="cf-outline-panel">
        <div v-if="open" class="cf-outline__panel" data-cy="cf-outline-panel">
          <div class="cf-outline__title">Jump to</div>
          <CustomFormOutlineList
            :sections="sections"
            :active-question-key="activeQuestionKey"
            :open-question-keys="openQuestionKeys"
            @jump-section="emitJump('jump-section', $event)"
            @jump-question="emitJump('jump-question', $event)"
          />
        </div>
      </Transition>
      <button
        type="button"
        class="cf-outline__toggle"
        :aria-expanded="open"
        data-cy="cf-outline-toggle"
        @click="open = !open"
      >
        <i class="fas" :class="open ? 'fa-chevron-right' : 'fa-list-ul'"></i>
        <span class="cf-outline__toggle-label">Outline</span>
      </button>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from "vue";

import CustomFormOutlineList, {
  type OutlineSection
} from "./CustomFormOutlineList.vue";

// The full-page builder's shell for the outline. A surface already inside a tab
// uses CustomFormJumpMenu instead.
withDefaults(
  defineProps<{
    sections: OutlineSection[];
    activeQuestionKey?: string | number | null;
    openQuestionKeys?: (string | number)[];
  }>(),
  { activeQuestionKey: null, openQuestionKeys: () => [] }
);
const emit = defineEmits<{
  "jump-section": [key: string | number];
  "jump-question": [key: string | number];
}>();

const open = ref(false);

function emitJump(
  event: "jump-section" | "jump-question",
  key: string | number
) {
  open.value = false;
  emit(event, key);
}
</script>

<style scoped>
.cf-outline {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 40;
  display: flex;
  align-items: center;
}

.cf-outline__toggle {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 0.5rem;
  border: 1px solid rgb(226 232 240);
  border-right: none;
  border-radius: 0.75rem 0 0 0.75rem;
  background: #ffffff;
  color: #1d4ed8;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: -8px 0 20px -8px rgba(15, 23, 42, 0.22);
  transition:
    transform 150ms ease,
    box-shadow 150ms ease;
}
.cf-outline__toggle:hover {
  transform: translateX(-2px);
  box-shadow: -12px 0 24px -8px rgba(15, 23, 42, 0.3);
}
.cf-outline__toggle:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}
.cf-outline__toggle-label {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  letter-spacing: 0.03em;
}

.cf-outline__panel {
  width: 19rem;
  max-height: 70vh;
  overflow-y: auto;
  margin-right: 0.5rem;
  padding: 0.5rem;
  border-radius: 0.75rem;
  border: 1px solid rgb(226 232 240);
  background: #ffffff;
  box-shadow:
    0 20px 40px -12px rgba(15, 23, 42, 0.3),
    0 8px 16px -8px rgba(15, 23, 42, 0.16);
}
.cf-outline__title {
  padding: 0.25rem 0.625rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
}

.cf-outline-panel-enter-active,
.cf-outline-panel-leave-active {
  transition:
    opacity 160ms ease,
    transform 160ms ease;
  transform-origin: right center;
}
.cf-outline-panel-enter-from,
.cf-outline-panel-leave-to {
  opacity: 0;
  transform: translateX(12px) scale(0.98);
}

@media (prefers-reduced-motion: reduce) {
  .cf-outline__toggle,
  .cf-outline-panel-enter-active,
  .cf-outline-panel-leave-active {
    transition: none;
  }
}
</style>
